import React from 'react';
import type { DynamicWidgetConfig, QueryResult, PercentageAggregationResult } from '@/types/dashboard';
import type { VisualizationClickEvent } from './DynamicVisualization';

interface PercentageVisualizationProps {
  data: QueryResult;
  config: DynamicWidgetConfig;
  /** Callback when the percentage is clicked (DASH-24.2) */
  onSegmentClick?: (event: VisualizationClickEvent) => void;
}

/**
 * Get color for percentage based on goal
 */
function getPercentageColor(percentage: number | null, goal?: number): string {
  if (percentage === null) return 'text-muted-foreground';
  if (!goal) return 'text-primary'; // No goal = primary theme color
  return percentage >= goal ? 'text-green-500' : 'text-red-500';
}

/**
 * Percentage Visualization (DASH-6.2, DASH-24.2)
 * Displays percentage metric with optional goal progress bar
 * Wrapped in React.memo for performance
 * DASH-24.2: Added click handler for drill-down functionality
 */
export const PercentageVisualization = React.memo(function PercentageVisualization({
  data,
  config,
  onSegmentClick
}: PercentageVisualizationProps) {
  // Extract percentage aggregation result from QueryResult
  const percentageData = data.data as PercentageAggregationResult;

  const goal = config.goal?.target;

  // Check for no data state
  const hasNoData = percentageData.percentage === null || percentageData.denominator === 0;

  // Handle click on the percentage (DASH-24.2)
  const handleClick = () => {
    if (onSegmentClick) {
      onSegmentClick({
        value: null, // No specific segment for percentage widgets
        index: null,
        data: {
          percentage: percentageData.percentage,
          numerator: percentageData.numerator,
          denominator: percentageData.denominator,
        },
      });
    }
  };

  return (
    <div
      className="h-full flex flex-col items-center justify-center relative cursor-pointer group"
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      {/* Subtle background gradient for depth (matching NumberVisualization) */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent rounded-lg" />

      {/* Main Percentage Value (matching NumberVisualization text-4xl font-bold) */}
      <div
        className={`text-4xl font-bold mb-2 relative z-10 ${
          getPercentageColor(percentageData.percentage, goal)
        }`}
      >
        {percentageData.percentage !== null ? `${percentageData.percentage.toFixed(0)}%` : 'N/A'}
      </div>

      {/* Progress Bar (conditional - only when goal is set and data exists) */}
      {goal !== undefined && percentageData.percentage !== null && !hasNoData && (
        <div className="w-full max-w-xs mt-3 relative z-10">
          {/* Container bar (muted background) */}
          <div className="w-full bg-muted rounded-full h-3">
            {/* Filled portion (green/red based on goal) */}
            <div
              className={`h-full rounded-full transition-all duration-300 ${
                percentageData.percentage >= goal ? 'bg-green-500' : 'bg-red-500'
              }`}
              style={{ width: `${Math.min(percentageData.percentage, 100)}%` }}
            />
          </div>

          {/* Goal label */}
          <div className="text-xs text-muted-foreground mt-1 text-center">
            Goal: {goal}%
          </div>
        </div>
      )}

      {/* No Data State */}
      {hasNoData && (
        <div className="text-sm text-muted-foreground relative z-10 mt-2">
          No data available
        </div>
      )}
    </div>
  );
});
